import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { MatLegacyTableDataSource as MatTableDataSource } from "@angular/material/legacy-table";
import { take } from "rxjs";

import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import { GlobalDashboardEventsService } from "@modules/global-dashboard/services/global-dashboard-events.service";
import { EventsTableDetailItem } from "@modules/global-dashboard/models/global-dashboard-interface";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";
import { PermissionsService } from "@shared/services/permissions/permissions.service";

@Component({
	selector: "app-landing-page-events-table",
	templateUrl: "./landing-page-events-table.component.html",
	styleUrls: ["./landing-page-events-table.component.scss"]
})
export class LandingPageEventsTableComponent implements OnInit {
	private globalDashboardEventsService = inject(GlobalDashboardEventsService);
	private historyLogService = inject(HistoryLogSidePanelService);
	private featureManagementService = inject(FeatureManagementService);
	private permissionsService = inject(PermissionsService);

	fundingScreenEnabled = false;
	tppFundingScreenEnabled = false;
	disabledFundingScreen = false;

	ngOnInit(): void {
		this.featureManagementService
			.hasFeature(FeatureFlagCodes.FUNDING_SCREEN)
			.pipe(take(1))
			.subscribe(hasFeature => (this.fundingScreenEnabled = hasFeature));

		this.featureManagementService
			.hasFeature(FeatureFlagCodes.FUNDING_SCREEN_TPP)
			.pipe(take(1))
			.subscribe(hasFeature => (this.tppFundingScreenEnabled = hasFeature));

		this.permissionsService
			.disableFundingScreen()
			.subscribe(disabledFundingScreen => (this.disabledFundingScreen = disabledFundingScreen));
	}

	@Input() set events(events: EventsTableDetailItem[]) {
		this.dataSourceEvents.data = events;
	}

	@Input() set isClient(client: boolean) {
		this._isClient = client;
		this.headers = [
			"select",
			client ? "Paygroups" : "Customers",
			"Paycycle/run",
			"Payments",
			"Milestone",
			"Status",
			"Deadline",
			"More"
		];
	}

	@Output() updateStatus: EventEmitter<void> = new EventEmitter<void>();
	@Output() hideEvent: EventEmitter<EventsTableDetailItem> = new EventEmitter<EventsTableDetailItem>();
	@Output() selectEvent: EventEmitter<EventsTableDetailItem[]> = new EventEmitter<EventsTableDetailItem[]>();

	dataSourceEvents = new MatTableDataSource<EventsTableDetailItem>();
	headers: string[] = [];
	selectedEvents: EventsTableDetailItem[] = [];
	_isClient = false;

	updateMilestoneEventHistoryLogSubject(element: EventsTableDetailItem) {
		this.historyLogService.setElementData(element);
	}

	routeToPayments(row: EventsTableDetailItem): void {
		if (row.milestoneDetails.type === "NETS" || row.milestoneDetails.type === "PAYMENT") {
			this.globalDashboardEventsService.routeToSpecifiedScreen(
				"payments",
				row.payGroupDetails.id,
				row.payCycleDetails.id,
				row.group
			);
		} else if (row.milestoneDetails.type === "G2N_FILE" || row.milestoneDetails.type === "DATA_INPUT") {
			this.globalDashboardEventsService.routeToSpecifiedScreen(
				"paymentOrders",
				row.payGroupDetails.id,
				row.payCycleDetails.id,
				row.group
			);
		} else if (
			row.milestoneDetails.type === "FUNDING_DATE" &&
			!this.disabledFundingScreen &&
			this.enabledFundingScreen(row.group)
		) {
			this.globalDashboardEventsService.routeToSpecifiedScreen(
				"funding-date",
				row.payGroupDetails.id,
				row.payCycleDetails.id,
				row.group
			);
		}
	}

	/**
	 * Check if funding screen is enabled for NETS or TPP
	 * @param group: milestone group
	 * @returns boolean
	 */
	enabledFundingScreen(group: string): boolean {
		if (group.includes("NETS") && this.fundingScreenEnabled) {
			return true;
		} else if (group.includes("TPP") && this.tppFundingScreenEnabled) {
			return true;
		} else {
			return false;
		}
	}

	isHidingDisabled(row: EventsTableDetailItem): boolean {
		if (row.milestoneDetails.type == "FUNDING_DATE") {
			return row.milestoneDetails.status == "COMPLETED";
		} else {
			return row.milestoneDetails.status != "PENDING";
		}
	}

	hideEventHandler(row: EventsTableDetailItem): void {
		this.hideEvent.emit(row);
	}

	selectEventHandler(row: EventsTableDetailItem, checked: boolean): void {
		if (checked) {
			this.selectedEvents.push(row);
		} else {
			this.selectedEvents = this.selectedEvents.filter(item => item !== row);
		}
		this.selectEvent.emit(this.selectedEvents);
	}

	hasCalendarError(payCycleId: string, payGroupId: string): boolean {
		return this.historyLogService
			.milestoneHistoryLogCalendarErrorItems()
			.some(errorList => errorList.payCycleId === payCycleId && errorList.payGroupId === payGroupId);
	}

	/**
	 * Redirect user from Global Dashboard to Dashboard page
	 * @param paygroupId: Customer paygroup ID
	 * @param payCycleId: Customer paycycle ID
	 * @param milestoneGroup: Customer event milestone group
	 */
	goToDashboard(paygroupId: string, payCycleId: string, milestoneGroup: string): void {
		this.globalDashboardEventsService.navigateToDashboard([paygroupId], payCycleId, milestoneGroup);
	}
}
